import Dexie, { type Table } from 'dexie';
import type { Card, Lesson } from '../content/cardSchema';

export type { Card, Lesson } from '../content/cardSchema';

// A "profile" = one kid. Each kid has their own SRS state, progression, etc.
export interface Profile {
  id: string;
  name: string;
  avatar: string;            // emoji or sprite key
  ageBand: '4-5' | '6-7' | '8+';
  createdAt: number;
  // Per-profile difficulty knobs derived from the spike-1 calibration.
  scoreFloor: number;        // Below this, treat as fumble. Default 30.
  toneStrict: boolean;       // Phase 1 (Azure) only — kept here to forward-port.
}

// SM-2-ish state, capped at 14-day interval per the plan.
export interface SRSState {
  id?: number;               // auto-increment
  profileId: string;
  cardId: string;
  ease: number;              // SM-2 ease factor, starts 2.5
  intervalDays: number;      // current interval; capped at 14
  dueAt: number;             // epoch ms
  reps: number;              // total successful reps
  lapses: number;            // total misses
  lastAttemptAccuracy: number; // 0–100, used by zone-unlock gate
  lastSeenAt: number;
}

export interface Attempt {
  id?: number;
  profileId: string;
  cardId: string;
  sessionId: string;
  ts: number;
  score: number;             // 0–100 from scoring.ts
  heardHanzi: string;
  heardPinyin: string;       // tone-num form
  latencyMs: number;
  outcome: 'critical' | 'hit' | 'glance' | 'miss' | 'fumble';
  retryStep: 0 | 1 | 2;      // failure-scaffold step that produced this attempt
  passed: boolean;
}

export interface Session {
  id: string;
  profileId: string;
  startedAt: number;
  endedAt?: number;
  encountersCompleted: number;
  xpGained: number;
}

// Per-profile progression / inventory.
export interface Progress {
  profileId: string;         // primary key
  level: number;
  xp: number;
  maxHp: number;
  hp: number;
  coins: number;
  hatSlot: string;           // current hat id, e.g. "starter"
  wandSlot: string;          // current wand id
  unlockedHats: string[];
  unlockedWands: string[];
  unlockedSpells: string[];  // always includes "zap"
  unlockedZoneIds: string[]; // lesson ids
  streakDays: number;
  lastPlayedDate: string;    // YYYY-MM-DD
  sageWhisperUsedToday: number;
  dailyQuestId?: string;
  dailyQuestProgress?: number;
}

export interface Settings {
  key: 'global';
  selectedProfileId?: string;
  audioEnabled: boolean;
  pinyinReveal: 'on-miss' | 'always' | 'never';
  asrProvider: 'web-speech' | 'azure';
  azureKey?: string;
  azureRegion?: string;
  monthlyAsrBudgetCents?: number;
}

class WizardSpeakDB extends Dexie {
  profiles!: Table<Profile, string>;
  lessons!: Table<Lesson, string>;
  cards!: Table<Card, string>;
  srs!: Table<SRSState, number>;
  attempts!: Table<Attempt, number>;
  sessions!: Table<Session, string>;
  progress!: Table<Progress, string>;
  settings!: Table<Settings, string>;

  constructor() {
    super('wizardspeak');
    this.version(1).stores({
      profiles: 'id, createdAt',
      lessons: 'id, createdAt, zoneTheme',
      cards: 'id, lessonId, tag, hanzi',
      // Compound index lets us load all SRS state for a profile efficiently.
      srs: '++id, &[profileId+cardId], profileId, dueAt',
      attempts: '++id, profileId, sessionId, cardId, ts',
      sessions: 'id, profileId, startedAt',
      progress: 'profileId',
      settings: 'key',
    });
  }
}

export const db = new WizardSpeakDB();

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const newId = () =>
  // crypto.randomUUID is available on iOS Safari 16.4+
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export async function getSettings(): Promise<Settings> {
  const existing = await db.settings.get('global');
  if (existing) return existing;
  const fresh: Settings = {
    key: 'global',
    audioEnabled: true,
    pinyinReveal: 'on-miss',
    asrProvider: 'web-speech',
  };
  await db.settings.put(fresh);
  return fresh;
}

export async function ensureProgress(profileId: string): Promise<Progress> {
  const existing = await db.progress.get(profileId);
  if (existing) return existing;
  const fresh: Progress = {
    profileId,
    level: 1,
    xp: 0,
    maxHp: 5,
    hp: 5,
    coins: 0,
    hatSlot: 'starter',
    wandSlot: 'starter',
    unlockedHats: ['starter'],
    unlockedWands: ['starter'],
    unlockedSpells: ['zap'],
    unlockedZoneIds: [],
    streakDays: 0,
    lastPlayedDate: '',
    sageWhisperUsedToday: 0,
  };
  await db.progress.put(fresh);
  return fresh;
}
